import { IDecaCompanyFormValue, IStopLocationData } from './deca-forms.model';

export type DecaFormWarningField = 'weight' | 'plates' | 'carrier';

export interface IDecaFormWarningValues {
  reference?: string | null;
  tractorPlateNumber?: string | null;
  carrier?: IDecaCompanyFormValue | null;
  segments?: ReadonlyArray<{ netWeight?: number | null }> | null;
}

export interface IDecaFormWarnings {
  hasMissingNetWeight: boolean;
  hasMissingTractorPlate: boolean;
  hasMissingCarrierData: boolean;
  hasMissingReference: boolean;
}

const DECA_FORM_WARNING_LABELS: Record<DecaFormWarningField, string> = {
  weight: 'Peso',
  plates: 'Matrículas',
  carrier: 'Transportista efectivo',
};

export function hasDecaCompanyData(formValue: IDecaCompanyFormValue, location: IStopLocationData | null): boolean {
  if (location?.postalCode || location?.city || location?.stateCode || location?.countryCode) {
    return true;
  }

  return !!(
    formValue.companyName?.trim() ||
    formValue.taxId?.trim() ||
    formValue.street?.trim() ||
    formValue.contactEmail?.trim() ||
    formValue.contactFirstName?.trim() ||
    formValue.contactLastName?.trim() ||
    formValue.contactPhone?.trim()
  );
}

/**
 * Fields that are not mandatory in the form but are needed for a complete and valid DeCA. The
 * warnings are only informative: the document can still be saved.
 */
export function getDecaFormWarnings(params: {
  formValue: IDecaFormWarningValues;
  carrierLocation: IStopLocationData | null;
  isActingShipper: boolean;
  isServiceStarted: boolean;
}): IDecaFormWarnings {
  const { formValue, carrierLocation, isActingShipper, isServiceStarted } = params;

  return {
    hasMissingNetWeight: (formValue.segments ?? []).some((segment) => !segment.netWeight),
    hasMissingTractorPlate: !formValue.tractorPlateNumber?.trim(),
    hasMissingCarrierData:
      isActingShipper &&
      !isServiceStarted &&
      !!formValue.carrier &&
      !hasDecaCompanyData(formValue.carrier, carrierLocation),
    hasMissingReference: !formValue.reference?.trim(),
  };
}

export function hasDecaFormWarnings(warnings: IDecaFormWarnings): boolean {
  return (
    warnings.hasMissingNetWeight ||
    warnings.hasMissingTractorPlate ||
    warnings.hasMissingCarrierData ||
    warnings.hasMissingReference
  );
}

export function buildDecaFormWarningMessage(warnings: IDecaFormWarnings): string {
  const missingFields: DecaFormWarningField[] = [];

  if (warnings.hasMissingNetWeight) missingFields.push('weight');
  if (warnings.hasMissingTractorPlate) missingFields.push('plates');
  if (warnings.hasMissingCarrierData) missingFields.push('carrier');

  const sections: string[] = [];

  if (missingFields.length) {
    const items = missingFields.map((field) => `<li><strong>${DECA_FORM_WARNING_LABELS[field]}</strong></li>`).join('');

    sections.push(
      'Puede guardar el documento con la información actual, pero recuerde que los siguientes datos son ' +
        `obligatorios para que un DeCA esté completo y sea válido:<ul>${items}</ul>`,
    );
  }

  if (warnings.hasMissingReference) {
    sections.push(
      '<p>La <strong>referencia interna</strong> no es obligatoria, pero le puede ayudar a identificar mejor cada DeCA.</p>',
    );
  }

  return sections.join('');
}
